
import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from "@/integrations/supabase/client";

interface Partner {
  name: string;
  logoUrl: string;
}

export const PressPartnersSection = () => {
  const { language } = useLanguage();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const fetchPartners = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase.storage.from('partner-logos').list();
        if (error) throw error;
        
        // Map file data to partner objects with public URLs
        const partnerData = data.map(file => {
          // Convert filename to partner name (remove extension and replace hyphens with spaces)
          const name = file.name
            .split('.')[0]
            .replace(/_/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
            
          return {
            name,
            logoUrl: supabase.storage.from('partner-logos').getPublicUrl(file.name).data.publicUrl
          };
        });
        
        setPartners(partnerData);
      } catch (error) {
        console.error('Error fetching partner logos:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPartners();
  }, []);
  
  return (
    <section className="py-12 px-4">
      <div className="container mx-auto max-w-4xl">
        <h2 className="text-2xl md:text-3xl font-bold mb-6">
          🤝 {language === 'fr' ? 'Ils collaborent avec nous' : 'They collaborate with us'}
        </h2>
        
        <p className="text-lg text-muted-foreground mb-8">
          {language === 'fr' ? (
            "Studios et partenaires culturels ayant collaboré avec Recut"
          ) : (
            "Studios and cultural partners who have collaborated with Recut"
          )}
        </p>
        
        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {[1, 2, 3, 4, 5].map((index) => (
              <div 
                key={index} 
                className="aspect-video flex items-center justify-center rounded-md bg-muted animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 lg:gap-8">
            {partners.map((partner, index) => (
              <div 
                key={index} 
                className="aspect-video bg-white/5 dark:bg-black/10 rounded-md p-4 flex items-center justify-center hover:bg-white/10 dark:hover:bg-black/20 transition-all duration-300"
              >
                <img 
                  src={partner.logoUrl} 
                  alt={partner.name} 
                  className="max-w-full max-h-full object-contain"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
